<!--
  The next N weeks for this audience: what's booked, and which weeks are empty.

  "Booked" means a campaign whose Customer.io work areas overlap this audience's — so an industry sees its
  topics' campaigns, which is who actually receives them. Open slots come from the same scheduler caps that
  will be enforced when a campaign is saved, so a week showing 0 open email slots really is full.
-->
<template>
  <div>
    <div class="text-caption grey--text mb-3">
      {{ summary }}
    </div>

    <v-timeline
      dense
      class="pt-0"
    >
      <v-timeline-item
        v-for="week in weeks"
        :key="week.weekStart"
        small
        :color="week.isHole ? 'grey lighten-1' : 'mcsprimary'"
        :icon="week.isHole ? 'mdi-calendar-blank-outline' : ''"
      >
        <div class="d-flex align-center flex-wrap">
          <strong class="text-body-2 mr-3">Week of {{ weekLabel(week.weekStart) }}</strong>
          <v-chip
            v-if="week.emailOpenSlots"
            x-small
            outlined
            class="mr-1"
          >
            {{ week.emailOpenSlots }} email slot{{ week.emailOpenSlots === 1 ? '' : 's' }} open
          </v-chip>
          <v-chip
            v-if="week.socialOpenSlots"
            x-small
            outlined
          >
            {{ week.socialOpenSlots }} social slot{{ week.socialOpenSlots === 1 ? '' : 's' }} open
          </v-chip>
        </div>

        <div
          v-if="week.isHole"
          class="text-caption grey--text"
        >
          Nothing planned for this audience.
        </div>

        <div
          v-for="entry in week.entries"
          :key="entry.campaignId + '-' + entry.scheduledSend"
          class="d-flex align-center mt-1"
        >
          <PlatformIcon
            v-if="entry.platform"
            :platform="entry.platform"
            :size="18"
            class="mr-2"
          />
          <v-icon
            v-else
            small
            class="mr-2"
          >
            mdi-email-outline
          </v-icon>

          <a
            class="text-body-2 mr-2"
            href="#"
            @click.prevent="openCampaign(entry.campaignId)"
          >{{ entry.campaignName }}</a>

          <v-chip
            x-small
            :color="itemStatus(entry.status).color"
            dark
            class="mr-2"
          >
            {{ itemStatus(entry.status).label }}
          </v-chip>

          <span class="text-caption grey--text">{{ sendLabel(entry.scheduledSend) }}</span>

          <v-chip
            v-if="entry.fromPlan"
            x-small
            color="primary"
            dark
            class="ml-2"
            title="Built from this audience's plan"
          >
            from plan
          </v-chip>

          <v-chip
            v-if="!entry.directTarget"
            x-small
            outlined
            class="ml-2"
            title="Targets a related subject, but reaches this audience"
          >
            indirect
          </v-chip>
        </div>
      </v-timeline-item>
    </v-timeline>
  </div>
</template>

<script>
import PlatformIcon from '@/components/common/PlatformIcon';
import { itemStatus } from '@/helpers/marketing';

export default {
  name: 'AudienceRoadmap',
  components: { PlatformIcon },
  props: {
    roadmap: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    weeks() {
      return this.roadmap.weeks || [];
    },
    summary() {
      const total = this.weeks.reduce((n, w) => n + (w.entries || []).length, 0);
      const holes = this.weeks.filter((w) => w.isHole).length;
      if (!this.weeks.length) return '';
      if (!total) return `Nothing is scheduled for this audience in the next ${this.weeks.length} weeks.`;

      const fromPlan = this.weeks.reduce(
        (n, w) => n + (w.entries || []).filter((e) => e.fromPlan).length,
        0
      );
      const planNote = fromPlan ? `, ${fromPlan} from the plan` : '';
      return `${total} scheduled item${total === 1 ? '' : 's'} over ${this.weeks.length} weeks${planNote}, with ${holes} empty week${holes === 1 ? '' : 's'}.`;
    }
  },
  methods: {
    itemStatus,
    weekLabel(iso) {
      return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    },
    sendLabel(iso) {
      return new Date(iso).toLocaleString(undefined, {
        weekday: 'short',
        hour: 'numeric',
        minute: '2-digit'
      });
    },
    // New tab, same as the suggestion cards: the roadmap is the thing being reviewed, so following a campaign
    // out of it and back again loses the admin's place.
    openCampaign(id) {
      const href = this.$router.resolve(`/marketing/campaigns/${id}`).href;
      window.open(href, '_blank', 'noopener');
    }
  }
};
</script>
